import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ArrowRight, Moon } from 'lucide-react';
import logoWhite from '../../assets/images/footer/Nappr-Logo-WhiteZs.png';
import facebook from '../../assets/images/footer/facebook.png';
import instagram from '../../assets/images/footer/instagram.png';
import x from '../../assets/images/footer/x.png';
import tiktok from '../../assets/images/footer/tiktok.png';
import visa from '../../assets/images/footer/cc-visa.png';
import mastercard from '../../assets/images/footer/cc-mastercard.png';
import applePay from '../../assets/images/footer/cc-apple-pay.png';
import appleBadge from '../../assets/images/footer/apple-badge.png';
import googlePlayBadge from '../../assets/images/footer/google-play-badge.png';
import { subscribeToNewsletter } from '../../api/content';
import { getErrorMessage, getStatus } from '../../api/errors';
import { toast } from '../../ui';
import { APP_STORE_URL, PLAY_STORE_URL } from '../../content/site';
import { useNearbySearchLink } from '../../features/search/useNearbySearchLink';
import styles from './Footer.module.css';

const SOCIALS = [
  { href: 'https://www.facebook.com/NapprApp/', label: 'Facebook', icon: facebook },
  { href: 'https://www.instagram.com/napprapp/', label: 'Instagram', icon: instagram },
  { href: 'https://www.x.com/NapprApp', label: 'X', icon: x },
  { href: 'https://www.tiktok.com/@NapprApp', label: 'TikTok', icon: tiktok },
];

const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

function Newsletter() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  const subscribe = useMutation({
    mutationFn: subscribeToNewsletter,
    onSuccess: () => {
      setEmail('');
      toast.success('You’re subscribed!', { description: 'Deals and nap tips are on their way.' });
    },
    onError: (err) => {
      if (getStatus(err) === 409) {
        toast.info('You’re already subscribed', { description: 'Thanks for sticking with us.' });
      } else {
        toast.error('Couldn’t subscribe', { description: getErrorMessage(err) });
      }
    },
  });

  const onSubmit = (event) => {
    event.preventDefault();
    const value = email.trim();
    if (!isEmail(value)) {
      setError('Enter a valid email address');
      return;
    }
    setError('');
    subscribe.mutate(value);
  };

  return (
    <form className={styles.newsletter} onSubmit={onSubmit} noValidate>
      <label htmlFor="footer-email" className={styles.colTitle}>
        Get deals &amp; nap tips
      </label>
      <p className={styles.newsText}>Occasional emails. Unsubscribe anytime.</p>
      <div className={styles.newsField}>
        <input
          id="footer-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? 'footer-email-error' : undefined}
        />
        <button type="submit" aria-label="Subscribe" disabled={subscribe.isPending}>
          <ArrowRight />
        </button>
      </div>
      {error && (
        <p id="footer-email-error" className={styles.newsError} role="alert">
          {error}
        </p>
      )}
    </form>
  );
}

export function Footer() {
  const findLink = useNearbySearchLink();

  return (
    <footer className={styles.footer}>
      <div className={styles.glow} aria-hidden="true" />
      <div className={`container-wide ${styles.top}`}>
        <div className={styles.brandCol}>
          <img src={logoWhite} alt="Nappr" className={styles.logo} loading="lazy" />
          <p className={styles.tagline}>
            <Moon aria-hidden="true" /> Hotel rooms by the hour. Rest, recharge, repeat.
          </p>
          <div className={styles.badges}>
            <a href={APP_STORE_URL} target="_blank" rel="noreferrer">
              <img src={appleBadge} alt="Download on the App Store" loading="lazy" width="135" height="40" />
            </a>
            <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer">
              <img src={googlePlayBadge} alt="Get it on Google Play" loading="lazy" width="135" height="40" />
            </a>
          </div>
          <div className={styles.socials}>
            {SOCIALS.map((s) => (
              <a key={s.label} href={s.href} target="_blank" rel="noreferrer" aria-label={`Nappr on ${s.label}`} className={styles.social}>
                <img src={s.icon} alt="" loading="lazy" />
              </a>
            ))}
          </div>
        </div>

        <nav className={styles.col} aria-label="Explore">
          <p className={styles.colTitle}>Explore</p>
          <Link to={findLink.to} state={findLink.state}>
            Find a room
          </Link>
          <Link to="/hotels/new-york">New York</Link>
          <Link to="/hotels/los-angeles">Los Angeles</Link>
          <Link to="/hotels/london">London</Link>
          <Link to="/blog">Blog</Link>
        </nav>

        <nav className={styles.col} aria-label="Company">
          <p className={styles.colTitle}>Company</p>
          <Link to="/team">Team</Link>
          <Link to="/press">Press</Link>
          <Link to="/careers">Careers</Link>
          <Link to="/contact-us">Contact us</Link>
          <a href="https://nappr.statuspage.io" target="_blank" rel="noopener noreferrer">
            Status
          </a>
        </nav>

        <nav className={styles.col} aria-label="Hosts and support">
          <p className={styles.colTitle}>Hosts &amp; help</p>
          <Link to="/host-with-us">Host with us</Link>
          <Link to="/self-host">Self-host portal</Link>
          <Link to="/faq">FAQ</Link>
          <Link to="/booking-policy">Booking policy</Link>
        </nav>

        <Newsletter />
      </div>

      <div className={`container-wide ${styles.bottom}`}>
        <div className={styles.payments} aria-label="Supported payment methods">
          <img loading="lazy" src={visa} alt="Visa" />
          <img loading="lazy" src={mastercard} alt="Mastercard" />
          <img loading="lazy" src={applePay} alt="Apple Pay" />
        </div>
        <nav className={styles.legal} aria-label="Legal">
          <Link to="/terms">Terms</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/booking-policy">Booking policy</Link>
          <Link to="/sitemap">Site map</Link>
        </nav>
        <p className={styles.copyright}>© 2026 Forty Winks LLC</p>
        <iframe
          className={styles.travelBadge}
          src="https://cdn2.yoshki.com/badgeframe?17"
          width="300"
          height="94"
          title="Travel Tech badge"
          loading="lazy"
        />
      </div>
    </footer>
  );
}
